import React, { useState, useEffect, useRef, useCallback } from "react";
import { Dropdown, DropdownToggle, DropdownMenu } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getJobApplicationsCounts, getJobApplications } from "../../helpers/realbackend_helper";

const POLL_INTERVAL = 30_000;

// UUID filename-г танина, хэрэв тийм бол файлын өргөтгөлийг л харуулна
const isUUID = (name = "") =>
  /^[0-9a-f]{8,}-[0-9a-f]{4,}-[0-9a-f]{4,}.*\.(pdf|docx|doc)$/i.test(name);

const shortFileName = (name = "") => {
  if (!name) return null;
  if (isUUID(name)) {
    const ext = name.split(".").pop().toUpperCase();
    return ext + " файл";
  }
  return name.length > 28 ? name.slice(0, 25) + "…" : name;
};

const fileIcon = (name = "") => {
  const ext = name.split(".").pop().toLowerCase();
  if (ext === "pdf") return { icon: "ri-file-pdf-line", color: "#e74c3c", bg: "#fdf0ef" };
  if (["doc", "docx"].includes(ext)) return { icon: "ri-file-word-line", color: "#2980b9", bg: "#eaf4fb" };
  return { icon: "ri-file-line", color: "#6c757d", bg: "#f3f4f6" };
};

const fmtTime = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  const Y = dt.getFullYear();
  const M = String(dt.getMonth() + 1).padStart(2, "0");
  const D = String(dt.getDate()).padStart(2, "0");
  const h = String(dt.getHours()).padStart(2, "0");
  const m = String(dt.getMinutes()).padStart(2, "0");
  return `${Y}-${M}-${D} ${h}:${m}`;
};

const fileSizeLabel = (bytes = 0) => {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const JobApplicationBadge = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [totalNew, setTotalNew] = useState(0);
  const [recentApps, setRecentApps] = useState([]);
  const [soundAllowed, setSoundAllowed] = useState(false);

  const prevTotalNewRef = useRef(0);
  const initialLoadRef = useRef(true);

  useEffect(() => {
    const allow = () => setSoundAllowed(true);
    document.addEventListener("click", allow, { once: true });
    return () => document.removeEventListener("click", allow);
  }, []);

  const playSound = useCallback(() => {
    if (!soundAllowed) return;
    new Audio("/sounds/notification.mp3").play().catch(() => {});
  }, [soundAllowed]);

  const fetchCounts = useCallback(async () => {
    try {
      const data = await getJobApplicationsCounts();
      if (!data || typeof data !== "object") return;
      const newTotal = Object.values(data).reduce((s, c) => s + (c.newCount || 0), 0);
      if (!initialLoadRef.current && newTotal > prevTotalNewRef.current) {
        const diff = newTotal - prevTotalNewRef.current;
        playSound();
        toast.info(`📋 Шинэ ажлын өргөдөл ирлээ! (${diff})`, {
          autoClose: 6000,
          onClick: () => navigate("/jobposts"),
          style: { cursor: "pointer" },
        });
      }
      prevTotalNewRef.current = newTotal;
      setTotalNew(newTotal);
    } catch {
      // чимээгүй
    } finally {
      initialLoadRef.current = false;
    }
  }, [playSound, navigate]);

  const fetchRecent = useCallback(async () => {
    try {
      const data = await getJobApplications({ status: "new" });
      setRecentApps(Array.isArray(data) ? data.slice(0, 6) : []);
    } catch {
      setRecentApps([]);
    }
  }, []);

  useEffect(() => {
    fetchCounts();
    const t = setInterval(fetchCounts, POLL_INTERVAL);
    return () => clearInterval(t);
  }, [fetchCounts]);

  const handleToggle = () => {
    const next = !isOpen;
    setIsOpen(next);
    if (next) {
      setSoundAllowed(true);
      fetchRecent();
    }
  };

  return (
    <>
      <style>{`
        .jab-item { transition: background .12s; }
        .jab-item:hover { background: #f0f8ff !important; }
        .jab-scroll::-webkit-scrollbar { width: 4px; }
        .jab-scroll::-webkit-scrollbar-thumb { background: #c8dff0; border-radius: 4px; }
      `}</style>

      <Dropdown isOpen={isOpen} toggle={handleToggle} className="topbar-head-dropdown ms-1 header-item">
        <DropdownToggle
          type="button" tag="button"
          className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle"
          title="Ажлын өргөдлүүд"
        >
          <i className="ri-file-user-line fs-22"></i>
          {totalNew > 0 && (
            <span className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-warning">
              {totalNew}
              <span className="visually-hidden">шинэ өргөдөл</span>
            </span>
          )}
        </DropdownToggle>

        <DropdownMenu className="dropdown-menu-end p-0" style={{ minWidth: 360, borderRadius: "0 0 14px 14px", overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.13)" }}>

          {/* ── Header ── */}
          <div style={{ background: "linear-gradient(135deg, #299cdb 0%, #1565c0 100%)", padding: "16px 18px 14px" }}>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <div style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className="ri-file-user-line" style={{ color: "#fff", fontSize: 16 }}></i>
                  </div>
                  <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>Ажлын өргөдлүүд</span>
                </div>
                <span style={{ color: "rgba(255,255,255,.8)", fontSize: 12 }}>
                  {totalNew > 0
                    ? `${totalNew} шинэ өргөдөл хүлээж байна`
                    : "Шинэ өргөдөл байхгүй байна"}
                </span>
              </div>
              {totalNew > 0 && (
                <div style={{
                  width: 42, height: 42, borderRadius: "50%",
                  background: "#f06548",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", fontWeight: 800, fontSize: 16,
                  boxShadow: "0 2px 8px rgba(240,101,72,.5)",
                }}>
                  {totalNew}
                </div>
              )}
            </div>
          </div>

          {/* ── Body ── */}
          <div className="jab-scroll" style={{ maxHeight: 340, overflowY: "auto", background: "#fff" }}>
            {recentApps.length === 0 ? (
              <div style={{ padding: "32px 16px", textAlign: "center" }}>
                <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#eff8ff", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
                  <i className="ri-file-user-line" style={{ fontSize: 24, color: "#a8d2f0" }}></i>
                </div>
                <p style={{ fontSize: 13, color: "#adb5bd", margin: 0 }}>Шинэ өргөдөл байхгүй байна</p>
              </div>
            ) : (
              recentApps.map((app, idx) => {
                const fi = fileIcon(app.fileName || "");
                const fname = shortFileName(app.fileName || "");
                const fsize = fileSizeLabel(app.fileSize);
                return (
                  <div
                    key={app._id}
                    className="jab-item"
                    style={{ padding: "12px 16px", borderBottom: "1px solid #f0f4f8", cursor: "pointer", background: "#fff" }}
                    onClick={() => { navigate("/jobposts"); setIsOpen(false); }}
                  >
                    <div className="d-flex gap-3 align-items-start">

                      {/* File icon */}
                      <div style={{
                        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                        background: fi.bg, color: fi.color,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 20,
                      }}>
                        <i className={fi.icon}></i>
                      </div>

                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Job title */}
                        <div style={{
                          fontSize: 13, fontWeight: 700, color: "#1a1a2e",
                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                          marginBottom: 3,
                        }}>
                          {app.jobTitle || "Ажлын зар"}
                        </div>

                        {/* Department */}
                        {app.department && (
                          <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 3 }}>
                            <i className="ri-building-2-line" style={{ fontSize: 11, color: "#6c757d" }}></i>
                            <span style={{ fontSize: 11.5, color: "#6c757d", fontWeight: 500 }}>{app.department}</span>
                          </div>
                        )}

                        {/* File info */}
                        {fname && (
                          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <i className={fi.icon} style={{ fontSize: 11, color: fi.color }}></i>
                            <span style={{ fontSize: 11, color: "#adb5bd" }}>{fname}</span>
                            {fsize && <span style={{ fontSize: 10, color: "#ced4da" }}>· {fsize}</span>}
                          </div>
                        )}
                      </div>

                      {/* Right side: time + badge */}
                      <div style={{ flexShrink: 0, textAlign: "right" }}>
                        <div style={{ fontSize: 10.5, color: "#adb5bd", marginBottom: 5, whiteSpace: "nowrap" }}>
                          {fmtTime(app.submittedAt)}
                        </div>
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 3,
                          background: "#fff3cd", color: "#856404",
                          borderRadius: 20, fontSize: 10, fontWeight: 700,
                          padding: "2px 7px",
                        }}>
                          <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#f7b84b", display: "inline-block" }}></span>
                          Шинэ
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* ── Footer ── */}
          <div style={{ padding: "12px 16px", borderTop: "1px solid #e9ecef", background: "#f8fafc" }}>
            <button
              className="btn w-100"
              style={{
                background: "linear-gradient(135deg, #299cdb 0%, #1565c0 100%)",
                color: "#fff", fontWeight: 600, fontSize: 13, borderRadius: 8,
                padding: "8px 0", border: "none", letterSpacing: ".3px",
              }}
              onClick={() => { navigate("/jobposts"); setIsOpen(false); }}
            >
              <i className="ri-briefcase-4-line me-2"></i>
              Ажлын зарын хуудас руу очих
            </button>
          </div>
        </DropdownMenu>
      </Dropdown>
    </>
  );
};

export default JobApplicationBadge;
